<script module lang="ts">
  import type { Snippet } from "svelte";

  export interface LanguagePickerMobileProps {
    children?: Snippet;
  }
</script>

<script lang="ts">
  import { page } from '$app/state';
  import { css } from "@agapi/ui/panda/css";
  import { NavbarMenuGroup, NavbarMenuItem, NavbarMenuSubmenu } from "@agapi/ui/navbar";
  import { icon } from "@agapi/ui/panda/patterns";
  import { Button } from "@agapi/ui/button";
  import { Combobox } from "@agapi/ui/combobox";
  import { Loader } from "@agapi/ui/loader";
  import { portal } from '@zag-js/svelte';
  import { browser } from '$app/environment';

  import { getLocaleFromUrl, getAvailableMarkets } from "../../utils";
  import { getLocale, setLocale, locales } from '$lib/parachute/runtime';
  import * as m from '$lib/parachute/messages';
  import { languagePickerMobileStyles } from "./language-picker-mobile.styles";

  let { country, language } = $state(getLocaleFromUrl(page.url));
  let isLoading = $state(false);

  const COOKIE_NAME = '__agapi_selected_country';

  /**
   * Set the country cookie
   */
  const setCountryCookie = (country: string): void => {
    if (!browser) return;
    // Set cookie that never expires
    document.cookie = `${COOKIE_NAME}=${country}; path=/; max-age=31536000; SameSite=Lax`;
  };

  const loadingOverlayStyles = css.raw({
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    width: '100vw',
    height: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backdropFilter: 'blur(5px)',
    backgroundColor: 'rgba(255, 255, 255, 0.6)',
    zIndex: 99999999
  });

  // Function to get flag URL based on country/language code
  const getFlagUrl = (flag?: string) => {
    switch(flag) {
      case 'GB': return '/flags/gb.svg';
      case 'EU': return '/flags/eu.svg';
      case 'SE': return '/flags/se.svg';
      case 'FI': return '/flags/fi.svg';
      case 'DK': return '/flags/dk.svg';
      case 'NO': return '/flags/no.svg';
      case 'ES': return '/flags/es.svg';
      case 'DE': return '/flags/de.svg';
      case 'en': return '/flags/gb.svg';
      case 'sv': return '/flags/se.svg';
      case 'fi': return '/flags/fi.svg';
      case 'da': return '/flags/dk.svg';
      case 'nb': return '/flags/no.svg';
      case 'es': return '/flags/es.svg';
      case 'de': return '/flags/de.svg';
      default: return '/flags/se.svg';
    }
  }

  // Function to change language
  const changeLanguage = (lang: string) => {
    isLoading = true;
    setLocale(lang);

    // Construct the new URL
    const languagePath = locales.includes(lang) ? lang === 'sv' ? '' : lang : '';
    const url = new URL(page.url.origin + '/' + languagePath + page.url.search);

    // Redirect to the new URL
    location.replace(url.toString());
  }

  // Function to change market/country
  const changeMarket = (countryCode: string) => {
    country = countryCode;
    isLoading = true;

    // Save the selected country in cookie
    setCountryCookie(countryCode.toUpperCase());

    const languagePath = locales.includes(language) ? language === 'sv' ? '' : language : '';
    const url = new URL(page.url.origin + '/' + languagePath + page.url.search);

    if (countryCode.toUpperCase() === 'SE') {
      url.searchParams.delete('country');
    } else {
      url.searchParams.set('country', countryCode.toLowerCase());
    }

    // Redirect to the new URL
    location.replace(url.toString());
  }

  const isMultiMarket = getAvailableMarkets().length > 1;
  const { children }: LanguagePickerMobileProps = $props();

  // Get styles from the language picker mobile styles
  const classes = languagePickerMobileStyles.raw();
</script>

<NavbarMenuGroup label={`${m[`${country?.toLowerCase?.()}Country`]?.()} / ${m[`${language?.toLowerCase?.()}Language`]?.()}`}>
  <NavbarMenuItem css={{ mb: '8' }} bold href="#" label={`${m[`${country?.toLowerCase?.()}Country`]?.()} / ${m[`${language?.toLowerCase?.()}Language`]?.()}`}>
    <NavbarMenuSubmenu label={`${m[`${country?.toLowerCase?.()}Country`]?.()} / ${m[`${language?.toLowerCase?.()}Language`]?.()}`}>
      <div class={css(classes.container)}>
        {#if locales.length > 1}
        <div class={css(classes.sectionContainer)}>
          <h5 class={css(classes.heading)}>{m.changeLang()}</h5>
          <div class={css(classes.buttonContainer)}>
            {#each locales as lang (lang)}
            <Button
            onclick={() => changeLanguage(lang)}
            size="xs"
            css={{
              ...classes.button,
              fontWeight: lang === getLocale() ? 'bold' : 'normal',
              color: lang === getLocale() ? 'primary.vibrant.foreground' : 'inherit'
            }}
                variant="secondary"
                >
                <span class={icon({ size: "md", height: "auto", mr: 2 })}>
                  <img src={getFlagUrl(lang)} alt={lang} />
                </span>
                {m[`${lang}Language`]?.()}
              </Button>
              {/each}
            </div>
          </div>
          {/if}

          {#if isMultiMarket}
          <div class={css(classes.sectionContainer)}>
            <h5 class={css(classes.heading)}>{m.changeMarket()}</h5>
            <Combobox
            positionerStyles={css.raw({ zIndex: '999!' })}
            inputStyles={css.raw({
              backgroundColor: 'white',
            })}
            items={getAvailableMarkets().map((countryCode) => ({
              value: countryCode,
              label: m[`${countryCode?.toLowerCase?.()}Country`]?.() ?? `${countryCode.toLowerCase()}Country is missing in translation`,
            }))}
            onValueChange={({ value: [ countryCode ] }) => {
              changeMarket(countryCode);
            }}
            placeholder={m.changeMarket()}
            />
          </div>
          {/if}
        </div>
    </NavbarMenuSubmenu>
    </NavbarMenuItem>
</NavbarMenuGroup>

{#if isLoading}
  <div use:portal class={css(loadingOverlayStyles)}>
    <Loader type="spinner" size="2xl" color="primary.500" />
  </div>
{/if}